import React, { useState, useEffect, useCallback } from 'react';
import { logsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, RefreshCw, Download, RotateCcw, History, ScrollText } from 'lucide-react';
import OltLogsPage from './OltLogsPage';
import useDarkReady from '../theme/useDarkReady';

// System Logs — tabbed shell: Change Ledger (below) + OLT Logs (OltLogsPage, admin only,
// matching its own /olt-logs route). Change Ledger is the audit trail of every create / update / delete / adjustment
// recorded by the app (styled after the LD.html reference page).

const ACTION_META = {
  create: { label: 'CREATED', color: 'var(--c-0e9e96)', bg: '#0E9E9618' },
  update: { label: 'UPDATED', color: 'var(--c-2f6fed)', bg: '#2F6FED18' },
  delete: { label: 'DELETED', color: 'var(--c-db4c5c)', bg: '#DB4C5C18' },
  note:   { label: 'NOTE',    color: 'var(--c-5b6ee8)', bg: '#5B6EE818' },
};

const CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'create', label: 'Created' },
  { key: 'update', label: 'Updated' },
  { key: 'delete', label: 'Deleted' },
  { key: 'note', label: 'Note' },
];

const ENTITY_LABELS = {
  customer: 'Customer', payment: 'Payment', user: 'User/Staff',
  catvPackage: 'CATV Package', broadbandPlan: 'Broadband Plan',
  splitter: 'Splitter', onuMap: 'ONU Mapping', onuLabel: 'ONU Label',
  companySettings: 'Company Settings', billingSettings: 'Billing Settings',
  integrationSettings: 'Integration Settings', messageTemplate: 'Message Template',
};

const ENTITY_FILTERS = [{ key: 'all', label: 'All Areas' }, ...Object.entries(ENTITY_LABELS).map(([key, label]) => ({ key, label }))];

const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
const normDigits = (s) => String(s || '').replace(/\D/g, '');

function dayLabel(iso) {
  const d = new Date(iso), today = new Date(), yest = new Date();
  yest.setDate(today.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yest)) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
}

const showVal = (v) => (v == null || v === '') ? '—' : String(v);

export default function SystemLogsPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get('tab') === 'olt' ? 'olt' : 'changes'));
  const tabs = [
    { key: 'changes', label: 'Change Ledger', icon: History },
    ...(isAdmin ? [{ key: 'olt', label: 'OLT Logs', icon: ScrollText }] : []),
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 4px', marginBottom: -1,
              background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.key ? 'var(--primary)' : 'transparent'}`,
              color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 600, fontSize: '.85rem', cursor: 'pointer',
            }}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>
      {tab === 'olt' && isAdmin ? <OltLogsPage /> : <ChangeLedger />}
    </div>
  );
}

function ChangeLedger() {
  useDarkReady();
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await logsAPI.getAll({ limit: 2000 });
      setLogs(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load change ledger');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const matches = (l) => {
    if (!search) return true;
    const q = search.toLowerCase(), qd = normDigits(search);
    return (l.customerName || '').toLowerCase().includes(q)
      || (l.username || '').toLowerCase().includes(q)
      || (qd.length > 0 && normDigits(l.mobile).includes(qd));
  };
  const filtered = logs.filter(l => matches(l)
    && (filter === 'all' || l.action === filter)
    && (entityFilter === 'all' || l.entity === entityFilter));

  const handleRestore = async (l) => {
    const confirmMsg = l.action === 'delete'
      ? `Restore the deleted ${ENTITY_LABELS[l.entity] || l.entity} "${l.customerName || l.username}"?`
      : `Restore ${l.field} of ${l.customerName || l.username} back to "${showVal(l.oldValue)}"?`;
    if (!window.confirm(confirmMsg)) return;
    setRestoring(l._id);
    try {
      const { data } = await logsAPI.restore(l._id);
      toast.success(data.message || 'Restored');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  const exportCsv = () => {
    const headers = ['TIME', 'AREA', 'ACTION', 'NAME', 'USERNAME/AC', 'MOBILE', 'FIELD', 'OLD VALUE', 'NEW VALUE', 'CHANGED BY'];
    const rows = filtered.map(l => [
      new Date(l.createdAt).toLocaleString('en-GB'), ENTITY_LABELS[l.entity] || l.entity, l.action, l.customerName || '', l.username || '',
      l.mobile || '', l.field || '', showVal(l.oldValue), showVal(l.newValue), l.changedByName || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `change-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Stats
  const uniqueContacts = new Set(logs.map(l => (l.username || '').toLowerCase() + '|' + normDigits(l.mobile))).size;
  const todayCount = logs.filter(l => new Date(l.createdAt).toDateString() === new Date().toDateString()).length;
  const lastActivity = logs.length ? fmtTime(logs[0].createdAt) : '—';

  // Group filtered by day
  let lastDay = null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <History size={22} /> Change Ledger
          </h1>
          <p className="page-subtitle">Every create, update, and delete across the whole app — customers, payments, staff, packages, fiber config, settings and more — restore any field or a deleted record</p>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { num: logs.length, lbl: 'Total Entries' },
          { num: uniqueContacts, lbl: 'Unique Records' },
          { num: todayCount, lbl: 'Logged Today' },
          { num: lastActivity, lbl: 'Last Activity' },
        ].map(s => (
          <div key={s.lbl} className="stat-card">
            <div className="stat-info">
              <h3 style={{ fontFamily: 'monospace' }}>{s.num}</h3>
              <p style={{ textTransform: 'uppercase', letterSpacing: '.08em', fontSize: '.7rem' }}>{s.lbl}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <div className="search-input-wrapper" style={{ flex: 1, minWidth: 220 }}>
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search by name, username or mobile number…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-control" style={{ width: 'auto', minWidth: 150 }} value={entityFilter} onChange={e => setEntityFilter(e.target.value)}>
          {ENTITY_FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={13} /> Refresh</button>
        <button className="btn btn-secondary btn-sm" onClick={exportCsv} disabled={!filtered.length}><Download size={13} /> Export CSV</button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {CHIPS.map(c => (
          <button key={c.key} onClick={() => setFilter(c.key)}
            style={{
              fontFamily: 'monospace', fontSize: '.72rem', letterSpacing: '.03em', textTransform: 'uppercase',
              padding: '7px 12px', borderRadius: 7, cursor: 'pointer',
              border: `1px solid ${filter === c.key ? 'var(--primary)' : 'var(--border)'}`,
              background: filter === c.key ? 'var(--primary-light, #dbeafe)' : 'var(--card-bg)',
              color: filter === c.key ? 'var(--primary)' : 'var(--text-muted)',
            }}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Ledger */}
      <div className="card"><div className="card-body" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
            <History size={36} style={{ opacity: .25, marginBottom: 10 }} />
            <div style={{ fontWeight: 700 }}>{logs.length === 0 ? 'No entries logged yet' : 'No matches'}</div>
            <p style={{ fontSize: '.83rem' }}>
              {logs.length === 0
                ? 'Changes across the app — customers, payments, staff, packages, fiber config, settings and more — will show up here automatically.'
                : 'Try a different name, username, mobile number, or filter.'}
            </p>
          </div>
        ) : (
          <div>
            {filtered.map(l => {
              const dl = dayLabel(l.createdAt);
              const showHeader = dl !== lastDay;
              lastDay = dl;
              const meta = ACTION_META[l.action] || ACTION_META.note;
              const hiddenValue = ['(hidden)', '(changed)', '(rotated)'].includes(l.oldValue);
              const canRestore = isAdmin && !l.restored && !hiddenValue && (
                (l.action === 'update' && l.oldValue !== undefined && l.oldValue !== null && l.oldValue !== '')
                || l.action === 'delete'
              );
              return (
                <React.Fragment key={l._id}>
                  {showHeader && (
                    <div style={{
                      fontFamily: 'monospace', fontSize: '.7rem', letterSpacing: '.1em', textTransform: 'uppercase',
                      color: 'var(--text-muted)', padding: '12px 18px 6px', borderBottom: '1px solid var(--border)',
                      background: 'var(--bg)', position: 'sticky', top: 0,
                    }}>
                      {dl}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12, padding: '10px 18px', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                    <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: meta.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{
                          fontFamily: 'monospace', fontSize: '.66rem', fontWeight: 700, letterSpacing: '.06em',
                          color: meta.color, background: meta.bg, borderRadius: 5, padding: '2px 7px',
                        }}>
                          {meta.label}
                        </span>
                        {l.entity && l.entity !== 'customer' && (
                          <span style={{
                            fontFamily: 'monospace', fontSize: '.64rem', letterSpacing: '.04em', textTransform: 'uppercase',
                            color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px',
                          }}>
                            {ENTITY_LABELS[l.entity] || l.entity}
                          </span>
                        )}
                        <b style={{ fontSize: '.86rem' }}>{l.customerName || '—'}</b>
                        {l.username && <span className="font-mono" style={{ fontSize: '.76rem', color: 'var(--primary)' }}>{l.username}</span>}
                        {l.mobile && <span style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>{l.mobile}</span>}
                        {l.restored && (
                          <span style={{ fontSize: '.68rem', color: 'var(--c-0e9e96)', fontWeight: 700 }}>✓ restored</span>
                        )}
                        <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: '.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                          {fmtTime(l.createdAt)}
                        </span>
                      </div>
                      <div style={{ fontSize: '.8rem', marginTop: 4, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {l.field && <span style={{ fontWeight: 600, color: 'var(--text)' }}>{l.field}</span>}
                        {(l.action === 'update' || l.action === 'note') ? (
                          <span className="font-mono" style={{ fontSize: '.78rem' }}>
                            <span style={{ background: '#DB4C5C14', color: 'var(--c-b91c1c)', borderRadius: 4, padding: '1px 6px', textDecoration: 'line-through' }}>{showVal(l.oldValue)}</span>
                            <span style={{ margin: '0 6px' }}>→</span>
                            <span style={{ background: '#0E9E9614', color: 'var(--c-0f766e)', borderRadius: 4, padding: '1px 6px' }}>{showVal(l.newValue)}</span>
                          </span>
                        ) : (
                          <span className="font-mono" style={{ fontSize: '.78rem' }}>{showVal(l.action === 'delete' ? l.oldValue : l.newValue)}</span>
                        )}
                        {l.changedByName && <span style={{ fontSize: '.72rem' }}>by {l.changedByName}</span>}
                      </div>
                    </div>
                    {canRestore && (
                      <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0, fontSize: '.72rem' }}
                        disabled={restoring === l._id}
                        onClick={() => handleRestore(l)}
                        title={`Restore ${l.field} to "${showVal(l.oldValue)}"`}>
                        <RotateCcw size={11} /> {restoring === l._id ? '…' : 'Restore'}
                      </button>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div></div>
    </div>
  );
}
